/**
 * WHAT ONE LINE OF A FRIENDS LIST IS ALLOWED TO SAY.
 *
 * The owner's report, in his words: *"If somebody is not in the player on the video
 * stream, whether paused or playing, then they shouldn't show up as watching whatever
 * in your friends list. That device was watching Spider-Man… when I left, it didn't
 * turn to 'online' — it should just say 'online'."*
 *
 * So: **"watching X" is a claim about a player, not about a room.** Being a member of
 * a room is not watching. A room having pinned a film is not watching. Somebody
 * standing in the kitchen with the tab open on Home is not watching, however true it
 * remains that their browser is still in the group.
 *
 * ── WHY THIS IS A MODULE AND NOT THREE `{#if}`s IN A COMPONENT ────────────────────
 *
 * Four places have to agree about it — the row's sentence, the row's Join button, the
 * order the list is sorted in, and the screen's own "nobody is watching anything"
 * heading — and when they were four separate expressions reading `presence.roomId`
 * they agreed only by luck. One function, four callers, and a test file that fails if
 * the sentence and the sort disagree.
 *
 * ── THE FIELD THIS TURNS ON, AND WHAT HAPPENS BEFORE IT EXISTS ────────────────────
 *
 * `FriendPresence.watching` is the server's answer and its contract is written out in
 * `protocol.ts`. The service now sends it — `RoomHub.watchingByUser()`, landed in the
 * integration pass — including an explicit `false` for a friend with nothing connected,
 * so an offline friend cannot be mistaken for an old server.
 *
 * It stays OPTIONAL all the same, because a client can be newer than the service it is
 * talking to: a browser holding an old tab open across a deploy, or a rollback to an
 * image from before the field existed. The fallback below is what that browser gets.
 *
 * When it is absent this falls back to the old meaning — a title implies watching —
 * rather than blanking every friend. A client cannot invent a fact the server did not
 * send, and answering "Online" for somebody who really is halfway through a film would
 * be a different wrong answer, not a smaller one. `unsubstantiated()` says which of the
 * two answers you are looking at, so the Friends screen can warn once instead of the
 * question being quietly forgotten.
 */

import type { FriendPresence } from './protocol';

/** The presence a row can be handed. Undefined is a friend the server said nothing about. */
export type MaybePresence = FriendPresence | undefined | null;

export type PresenceLine =
  /** No device of theirs is connected. */
  | { kind: 'offline' }
  /** Connected, and not in a player. **This is the answer the owner asked for.** */
  | { kind: 'online' }
  /** A player is attached but the room has not pinned anything yet. Rare and honest. */
  | { kind: 'in-room'; roomId: string }
  /** A player is attached to a room that has pinned a title. */
  | { kind: 'watching'; roomId: string; title: string; imdbId?: string; mediaKind?: string };

/**
 * Is a player attached to this friend's screen?
 *
 * `watching` is authoritative when the server sends it. Without it, the old
 * heuristic — the room has a title — stands in, and `unsubstantiated()` is true.
 */
export function isWatching(p: MaybePresence): boolean {
  if (!p || p.online !== true) return false;
  if (typeof p.watching === 'boolean') return p.watching;
  return typeof p.title === 'string' && p.title.trim() !== '';
}

/** True when `isWatching` had to guess, because the server sent no `watching`. */
export function unsubstantiated(p: MaybePresence): boolean {
  return !!p && p.online === true && typeof p.watching !== 'boolean';
}

/**
 * The room this friend can be joined into, if any.
 *
 * DELIBERATELY NOT the same question as `isWatching`. A friend who has wandered off
 * the watch screen is still in the room and the room is still joinable, so the button
 * stays; what goes away is the sentence CLAIMING they are watching something. The
 * capability and the claim are two different things and conflating them is what made
 * one wrong sentence look like a whole broken screen.
 */
export function joinableRoom(p: MaybePresence): string | null {
  if (!p || p.online !== true) return null;
  return typeof p.roomId === 'string' && p.roomId !== '' ? p.roomId : null;
}

/** The one sentence. */
export function presenceLine(p: MaybePresence): PresenceLine {
  if (!p || p.online !== true) return { kind: 'offline' };
  if (!isWatching(p)) return { kind: 'online' };

  const roomId = typeof p.roomId === 'string' && p.roomId !== '' ? p.roomId : '';
  const title = typeof p.title === 'string' ? p.title.trim() : '';
  if (title === '') return roomId === '' ? { kind: 'online' } : { kind: 'in-room', roomId };
  return {
    kind: 'watching',
    roomId,
    title,
    ...(p.imdbId === undefined ? {} : { imdbId: p.imdbId }),
    ...(p.kind === undefined ? {} : { mediaKind: p.kind })
  };
}

/**
 * Sort rank: people with something on first, then everyone else who is here, then the
 * people who are not. Reads `presenceLine` rather than `roomId` so the order and the
 * sentence can never disagree — before this, somebody browsing sorted to the top of
 * the list under a line that said "Online".
 */
export function presenceRank(p: MaybePresence): number {
  switch (presenceLine(p).kind) {
    case 'watching':
      return 0;
    case 'in-room':
      return 1;
    case 'online':
      return 2;
    default:
      return 3;
  }
}
